import clsx from "clsx";
import { MutableRefObject, useCallback, useMemo } from "react";

// MUI
import { Box, Fade } from "@mui/material";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { GridRowClassNameParams, GridValidRowModel } from "@mui/x-data-grid-pro";

// Components
import DataSmartTable from "@Layouts/components/Tables/DataSmartTable";
import CommonToolbar from "@Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import { TFTModelSwitch } from "../components/TFTModelSwitch";
import { LoadingSpinner } from "@/Layouts/components/LoadingSpinner";

// Hooks
import { useTechnitianDataTableConfig } from "@DataPages/components/ServiceOperations/Tabs/TechnicianFlaggedTime/hooks/useTechnicianDataTableConfig";

// Helpers
import { isAutoGeneratedFooter } from "@Layouts/components/Tables/DataSmartTable/helpers";

// Constants
import { tableKeys } from "@/Constants/tableKeys";

// Types
import { IRepairOrder, ITechnicianFlaggedTimeBase } from "@/DataPages/types/technicianFlaggedTime/model";
import { IAggregations, ITFTFooterModel } from "@DataPages/components/ServiceOperations/Tabs/TechnicianFlaggedTime/interfaces";

const resolveNoRowsOverlay = (title: string) => {
	switch (title) {
		case "Flagged Time Performance":
			return "No Flagged Time Performance Data";
		case "Pending Repair Orders and Jobs":
			return "No Pending Repair Orders and Jobs Data";
		default:
			return "No Performance Data";
	}
};

interface ITechnicianDataTableProps {
	apiRef: MutableRefObject<GridApiPremium>;
	title: string;
	rows: IRepairOrder[];
	footerModels: ITFTFooterModel;
	isHideTechName?: boolean;
	aggregations: IAggregations;
	hasNextPage: boolean;
	technicianPerformance: ITechnicianFlaggedTimeBase | null;
}

const filterJobStatus = (row: IRepairOrder) => {
	return !(!row.jobStatus || row.isParent);
};

export default function TechnicianDataTable(props: Readonly<ITechnicianDataTableProps>) {
	const { apiRef, title, rows, footerModels, isHideTechName, aggregations, hasNextPage, technicianPerformance } = props;

	const uniqueSTValues = useMemo(() => {
		const allJobStatus = rows.filter(filterJobStatus).map((row) => row.jobStatus);
		return Array.from(new Set(allJobStatus));
	}, [rows]);

	const { columns, groupingColDef } = useTechnitianDataTableConfig(apiRef, uniqueSTValues, isHideTechName, hasNextPage);
	const showTableConditions = [Boolean(columns?.length), Boolean(apiRef)];

	const getRowClassName = useCallback<(params: GridRowClassNameParams<GridValidRowModel>) => string>((params) => {
		return clsx({
			"!bg-primary [&_.MuiDataGrid-scrollbarFiller]:hover:!bg-primary [&_.MuiDataGrid-scrollbarFiller]:!border-t-primary":
				isAutoGeneratedFooter(params.id),
		});
	}, []);

	if (showTableConditions.some((condition) => !condition)) {
		return null;
	}

	return (
		<DataSmartTable
			filterStoreConfig={{
				key: tableKeys.DATA_PAGES.TECHNICIAN_FLAGGED_TIME,
			}}
			apiRef={apiRef}
			columns={columns}
			rows={rows || []}
			maxRows={19}
			enableCustomScrollbars
			hideFooter
			disableColumnMenu
			disableRowSelectionOnClick={true}
			disableMultipleRowSelection={true}
			checkboxSelection={false}
			columnVisibilityModel={{
				_id: false,
				employeeName: !isHideTechName,
			}}
			initialState={{
				aggregation: {
					model: footerModels,
				},
			}}
			slots={{
				noRowsOverlay: NoRowsOverlay,
				toolbar: CommonToolbar,
			}}
			slotProps={{
				toolbar: {
					customComponent: (
						<>
							{hasNextPage && (
								<Fade in={hasNextPage} timeout={400}>
									<Box className="my-[16px]">
										<LoadingSpinner label="Technician data is still loading..." className="items-center" />
									</Box>
								</Fade>
							)}
							<TFTModelSwitch technicianPerformance={technicianPerformance} />
						</>
					),
					filter: {
						hide: false,
					},
					search: {
						useQuickFilter: true,
					},
					expand: {
						hide: true,
					},
				},
				noRowsOverlay: {
					title: resolveNoRowsOverlay(title),
				},
			}}
			customProps={{
				tableContainerClassName:
					"[&_.MuiDataGrid-main]:mt-4 [&_.MuiDataGrid-cellEmpty]:border-none after:[&_.MuiDataGrid-topContainer]:!bg-disabled",
				customAggregationsFn: aggregations,
			}}
			treeData
			getTreeDataPath={(row) => (hasNextPage ? [(row as IRepairOrder)._id] : (row as IRepairOrder).hierarchy)}
			getRowClassName={getRowClassName}
			groupingColDef={groupingColDef}
		/>
	);
}
